/** A catalog record's cards in rows (the records' width rule, DESIGN.md §5 Zero-Re-Layout):
 *  each row is cards across the cards column, or two columns of cards side by side. Every
 *  column pins its cards' field grids to the column count its EDIT-mode width fits
 *  (`fieldGridColumns`), so entering edit mode — or the dock arriving — never moves a field to
 *  another row.
 *
 *  The record kind decides the rows (`activityCardRows`, `itineraryCardRows`) and draws each
 *  card; the loaded cards and the loading skeleton (`RecordPageSkeleton`) lay out through the
 *  same rows, so the skeleton stands where the cards will land. */
import { Box } from '@mui/material';
import { Fragment, type ReactNode } from 'react';

import { FIELD_GRID_VAR, fieldGridColumns } from './fieldGrid';

/** Between two cards, across and down. */
export const CARD_GAP_PX = 16;

/** One row of cards: across the column, or a left and a right column. */
export type RecordCardRow<K extends string> = { full: readonly K[] } | { left: readonly K[]; right: readonly K[] };

/** The width the cards column has in edit mode, and how many card columns it holds. */
export interface RecordCardColumns {
  /** The dock sits beside the cards. */
  docked: boolean;
  /** The cards' width in edit mode (0 = unmeasured). */
  editWidth: number;
  columns: 1 | 2;
}

export interface RecordCardRowsProps<K extends string> {
  rows: readonly RecordCardRow<K>[];
  /** The cards' width in edit mode; 0 = unmeasured — the field grids are not pinned. */
  editWidth: number;
  /** A card by its key (null: none). */
  card: (key: K) => ReactNode;
}

const COLUMN_SX = { display: 'grid', gap: `${CARD_GAP_PX}px`, minWidth: 0, alignContent: 'start' } as const;
const PAIR_SX = {
  display: 'grid',
  gap: `${CARD_GAP_PX}px`,
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
  alignItems: 'start',
} as const;

export function RecordCardRows<K extends string>({ rows, editWidth, card }: RecordCardRowsProps<K>) {
  const pin = (width: number): Record<string, string> =>
    editWidth > 0 ? { [FIELD_GRID_VAR]: fieldGridColumns(width) } : {};
  const column = (keys: readonly K[], width: number, key?: string) => (
    <Box key={key} sx={{ ...COLUMN_SX, ...pin(width) }}>
      {keys.map((k) => (
        <Fragment key={k}>{card(k)}</Fragment>
      ))}
    </Box>
  );
  const half = (editWidth - CARD_GAP_PX) / 2;
  return (
    <>
      {rows.map((row, i) =>
        'full' in row ? (
          column(row.full, editWidth, String(i))
        ) : (
          <Box key={i} sx={PAIR_SX}>
            {column(row.left, half)}
            {column(row.right, half)}
          </Box>
        ),
      )}
    </>
  );
}
